/**
 * LeftOperationalPanel - Job Context Sidebar
 * Extracted from PHSJobWorkspace for maintainability
 * Contains: Status, SLA, Contacts, Location, Scope, Benchmark, Financial, Linked Jobs
 */
import React, { useState, memo } from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, 
  User, 
  Wrench, 
  Phone,
  Mail,
  Building2,
  DollarSign,
  Link2,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { StatusChip, PriorityChip, SLABadge } from '@/components/phs/StatusChip';
import BenchmarkInsight from '@/components/phs/BenchmarkInsight';
import { computeWorkflowStatus } from '@/components/phs/WorkflowStatus';
import { 
  normalizePhone, 
  buildWhatsAppLink, 
  formatCurrency 
} from '@/utils/formatters';

// WhatsApp icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/**
 * Contact Card - Resident or Contractor contact details
 */
const ContactCard = memo(function ContactCard({ 
  type, 
  name, 
  phone, 
  email, 
  job, 
  onContactAction 
}) {
  const isResident = type === 'resident';
  const Icon = isResident ? User : Wrench;
  const iconColor = isResident ? 'text-emerald-600' : 'text-amber-600';
  const label = isResident ? 'Resident' : 'Contractor';
  
  const openWhatsApp = (phone, name) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return;
    const message = isResident
      ? `Hello ${name}, this is Park Home Support regarding job ${job.reference || job.id}. We have an update for you.`
      : `Hello, this is Park Home Support regarding job ${job.reference || job.id}. Please provide an update when possible.`;
    window.open(`https://wa.me/${normalized}?text=${encodeURIComponent(message)}`, '_blank');
    onContactAction?.({ type: 'whatsapp_log', message: `WhatsApp opened to ${type}`, visibility: 'internal' });
  };
  
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{label}</span>
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium text-slate-800">{name || 'Not provided'}</div>
        {phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-3 w-3 text-slate-400" />
            <span className="text-xs text-slate-600">{phone}</span>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-2">
            <Mail className="h-3 w-3 text-slate-400" />
            <span className="text-xs text-slate-600 truncate">{email}</span>
          </div>
        )}
        {phone && (
          <div className="flex gap-1.5 mt-2">
            <button
              onClick={() => window.location.href = `tel:${phone}`}
              className="flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-[10px] text-slate-600 hover:bg-slate-200"
            >
              <Phone className="h-2.5 w-2.5" /> Call
            </button>
            <button
              onClick={() => openWhatsApp(phone, name)}
              className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-[10px] text-emerald-700 hover:bg-emerald-200"
            >
              <WhatsAppIcon className="h-2.5 w-2.5" /> WhatsApp
            </button>
            {email && (
              <button
                onClick={() => window.location.href = `mailto:${email}`}
                className="flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-[10px] text-slate-600 hover:bg-slate-200"
              >
                <Mail className="h-2.5 w-2.5" /> Email
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Location Card - Park/Plot details
 */
const LocationCard = memo(function LocationCard({ job }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="h-4 w-4 text-blue-600" />
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Location</span>
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium text-slate-800">{job.park_name || job.park || 'Not specified'}</div>
        {(job.plot_number || job.plot) && (
          <div className="text-xs text-slate-600">Plot {job.plot_number || job.plot}</div>
        )}
        {job.address && (
          <div className="text-xs text-slate-500">{job.address}</div>
        )}
      </div>
    </div>
  );
});

/**
 * Scope Card - Job description
 */
const ScopeCard = memo(function ScopeCard({ job }) {
  const [expanded, setExpanded] = useState(false);
  const scopeSummary = job.description || job.scope_summary || job.issue_description || '';
  const truncatedScope = scopeSummary.length > 150 ? scopeSummary.substring(0, 150) + '...' : scopeSummary;
  
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-purple-600" />
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Scope</span>
        </div>
        {scopeSummary.length > 150 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-emerald-600 hover:text-emerald-700"
          >
            {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">
        {expanded ? scopeSummary : truncatedScope}
      </p>
    </div>
  );
});

/**
 * Financial Card - Quote/Invoice summary
 */
const FinancialCard = memo(function FinancialCard({ job }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="h-4 w-4 text-teal-600" />
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Financial</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-slate-400 text-[10px]">Quote</div>
          <div className="font-semibold text-slate-700">{formatCurrency(job.quote_value || job.approved_quote_value)}</div>
        </div>
        <div>
          <div className="text-slate-400 text-[10px]">Invoice</div>
          <div className="font-semibold text-slate-700">{formatCurrency(job.invoice_value)}</div>
        </div>
        <div>
          <div className="text-slate-400 text-[10px]">PO Number</div>
          <div className="font-medium text-slate-600">{job.po_number || '-'}</div>
        </div>
        <div>
          <div className="text-slate-400 text-[10px]">Status</div>
          <div className="font-medium text-slate-600">{job.invoice_status || 'Pending'}</div>
        </div>
      </div>
    </div>
  );
});

/**
 * Linked Jobs Card
 */
const LinkedJobsCard = memo(function LinkedJobsCard({ job }) {
  if (!job.linked_jobs?.length && !job.parent_job_id) return null;
  
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="h-4 w-4 text-indigo-600" />
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Linked Jobs</span>
      </div>
      <div className="space-y-2">
        {job.parent_job_id && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Parent Job</span>
            <a href={`/wyldecrest/helpdesk/job/${job.parent_job_id}`} className="text-emerald-600 hover:underline">
              View
            </a>
          </div>
        )}
        {job.linked_jobs?.map((linked, idx) => (
          <div key={idx} className="flex items-center justify-between text-xs">
            <span className="text-slate-600">{linked.reference || linked.id}</span>
            <a href={`/wyldecrest/helpdesk/job/${linked.id}`} className="text-emerald-600 hover:underline">
              View
            </a>
          </div>
        ))}
      </div>
    </div>
  );
});

/**
 * Status & SLA Card
 */
const StatusSLACard = memo(function StatusSLACard({ job }) {
  const workflowStatus = computeWorkflowStatus(job);
  
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <StatusChip status={job.status} />
        <PriorityChip priority={job.priority} />
      </div>
      <SLABadge job={job} />
      
      {/* Awaiting With / Next Action */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-slate-400 uppercase text-[10px] font-medium">Awaiting</div>
            <div className="font-semibold text-slate-700">{workflowStatus.awaiting_with}</div>
          </div>
          <div>
            <div className="text-slate-400 uppercase text-[10px] font-medium">Next Action</div>
            <div className="font-medium text-slate-600 truncate">{workflowStatus.next_required_action}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Left Operational Panel - Main Export
 * Composes all context cards into the left sidebar
 */
function LeftOperationalPanel({ job, onContactAction }) {
  if (!job) return null;
  
  // Extract contact details
  const residentPhone = job.resident_phone || job.contact_phone || job.phone;
  const residentEmail = job.resident_email || job.contact_email || job.email;
  const residentName = job.resident_name || job.contact_name || 'Not provided';
  
  const contractorPhone = job.contractor_phone || job.assigned_contractor?.phone;
  const contractorEmail = job.contractor_email || job.assigned_contractor?.email;
  const contractorName = job.contractor_name || job.assigned_contractor?.company_name || job.assigned_contractor?.name || 'Not assigned';
  
  return (
    <div className="w-72 flex-shrink-0 space-y-3 overflow-y-auto pb-4" data-testid="operational-context">
      <StatusSLACard job={job} />
      
      <ContactCard 
        type="resident"
        name={residentName}
        phone={residentPhone}
        email={residentEmail}
        job={job}
        onContactAction={onContactAction}
      />
      
      {job.assigned_contractor_id && (
        <ContactCard 
          type="contractor"
          name={contractorName}
          phone={contractorPhone}
          email={contractorEmail}
          job={job}
          onContactAction={onContactAction}
        />
      )}
      
      <LocationCard job={job} />
      <ScopeCard job={job} />
      <BenchmarkInsight jobId={job.id} job={job} className="!rounded-2xl" />
      <FinancialCard job={job} />
      <LinkedJobsCard job={job} />
    </div>
  );
}

export default memo(LeftOperationalPanel);
export { 
  ContactCard, 
  LocationCard, 
  ScopeCard, 
  FinancialCard, 
  LinkedJobsCard, 
  StatusSLACard 
};
